import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Kernel, KernelStatus } from "@/kernel/Kernel";
import { TaskRunner, applyRun, cellById, type Notebook, type Task, type TaskResult, type TaskRun } from "@/kernel/runner";
import type { AssignmentView, WorkspaceData } from "@/app/notebookQueries";
import { useKernelFactory } from "./kernelContext";

export type RunInfo = { task: number; run: TaskRun; at: Date };

/** Сессия рабочего места: локальный черновик, ядро, запуски. Изменения
 *  черновика отдаются наружу через onChange (автосохранение). */
export function useNotebookSession(asg: AssignmentView, ws: WorkspaceData, onChange: (nb: Notebook, results: Record<string, TaskResult>) => void) {
  const factory = useKernelFactory();
  const tasks = asg.manifest.tasks as Task[];
  const [notebook, setNotebook] = useState<Notebook>(ws.notebook);
  const [results, setResults] = useState<Record<string, TaskResult>>(ws.results ?? {});
  const [status, setStatus] = useState<KernelStatus>({ state: "idle" });
  const [runs, setRuns] = useState<Record<string, TaskRun>>({});
  const [lastRun, setLastRun] = useState<Date | null>(null);
  const [running, setRunning] = useState<number | "all" | null>(null);
  const kernel = useRef<Kernel | null>(null);
  const runner = useRef<TaskRunner | null>(null);
  const counter = useRef(1);
  const nbRef = useRef(notebook);
  const resRef = useRef(results);
  nbRef.current = notebook;
  resRef.current = results;

  const bootOpts = useMemo(() => ({ packages: asg.manifest.packages, datasets: asg.datasets.map((d) => ({ name: d.name, url: d.url })) }), [asg]);

  useEffect(() => {
    const k = factory();
    kernel.current = k;
    runner.current = new TaskRunner(k);
    const unsub = k.subscribe(setStatus);
    k.boot(bootOpts).catch(() => undefined);
    return () => {
      unsub();
      k.dispose();
    };
  }, [factory, bootOpts]);

  const commit = useCallback(
    (nb: Notebook, res: Record<string, TaskResult>) => {
      setNotebook(nb);
      setResults(res);
      onChange(nb, res);
    },
    [onChange],
  );

  const setCode = useCallback(
    (task: Task, code: string) => {
      const nb = { ...nbRef.current, cells: nbRef.current.cells.map((c) => (c.id === task.cell_id ? { ...c, source: splitLines(code) } : c)) };
      commit(nb, resRef.current);
    },
    [commit],
  );

  const record = (task: Task, run: TaskRun) => {
    const nb = applyRun(nbRef.current, task, run, counter.current);
    counter.current += 2;
    const res = { ...resRef.current, [String(task.n)]: { status: run.status, ...(run.error ? { error: run.error } : {}) } };
    nbRef.current = nb;
    resRef.current = res;
    setRuns((r) => ({ ...r, [String(task.n)]: run }));
    commit(nb, res);
    setLastRun(new Date());
  };

  const runTask = useCallback(async (task: Task) => {
    if (!runner.current) return;
    setRunning(task.n);
    try {
      record(task, await runner.current.runTask(nbRef.current, tasks, task));
    } finally {
      setRunning(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const runAll = useCallback(async () => {
    if (!runner.current) return;
    setRunning("all");
    try {
      await runner.current.runAll(nbRef.current, tasks, (t, r) => record(t, r));
    } finally {
      setRunning(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const restart = useCallback(async () => {
    runner.current?.reset();
    setRuns({});
    await kernel.current?.restart().catch(() => undefined);
  }, []);

  /** Заменить черновик целиком (после возврата из Colab). */
  const replace = useCallback((nb: Notebook, res: Record<string, TaskResult>) => {
    runner.current?.reset();
    setRuns({});
    setNotebook(nb);
    setResults(res);
  }, []);

  const code = (task: Task) => {
    const c = cellById(notebook, task.cell_id);
    return c ? (Array.isArray(c.source) ? c.source.join("") : c.source) : "";
  };

  return { tasks, notebook, results, status, runs, lastRun, running, runTask, runAll, restart, setCode, code, replace };
}

export function splitLines(code: string): string[] {
  if (!code) return [];
  const parts = code.split(/(?<=\n)/);
  return parts;
}
